import type { AdminManagement } from 'se-fe-data-access/ngin-admin-management'
import type { UserPersona } from 'se-fe-data-access/ngin-user-persona'
import type { Persona as UserServicePersona } from 'se-fe-data-access/user-service-persona'
import type { CurrentUser } from 'se-resource-types/lib/UserService/current-user'
import type { RoleAssignment } from 'se-resource-types/lib/UserService/role-assignment'
import type { VerifiedInformation } from 'se-resource-types/dist/lib/UserService'
import { AgeParser } from '@se-po/shared-data-access-parsers'

export type ProfileIdentityRoleAssignment = Pick<RoleAssignment, 'role'> & Partial<Pick<RoleAssignment, 'org_id'>>

export interface ProfileIdentityCurrentUser extends Pick<CurrentUser, 'id'> {
  role_assignments?: ProfileIdentityRoleAssignment[]
}

export interface ProfileIdentityContext {
  currentUser?: ProfileIdentityCurrentUser
  personaId?: string | number | null
  orgId?: string | number | null
  isClub?: boolean
  hasVerifiedInfoFeatureToggle?: boolean
}

export interface ProfileIdentity {
  currentUser?: ProfileIdentityCurrentUser
  personaId?: string | number | null
  orgId?: string | number | null
  persona?: UserPersona
  avatar?: string
  admin?: AdminManagement
  verifiedInfo?: VerifiedInformation | null
  verifiedInfoFields: string[]
  verifiedInfoMismatchedFields: string[]
  verifiedInfoMismatch: boolean
  hasVerifiedInfoFeatureToggle?: boolean
  isAdmin: boolean
  hasOwner: boolean
  editable: boolean
  pendingProfile: boolean
  inviteStatus: string
  age?: string
  dob?: string
  graduationYear?: string
  gender?: string
  address?: string
  email?: string
  emailId?: number
  phone?: string
  access?: string
  roles?: string
  title: string
  subtitle: string[]
  headerLoaded: boolean
}

export interface ProfileIdentityResources {
  persona?: UserPersona
  admin?: AdminManagement
  verifiedPersona?: UserServicePersona
  headerLoaded: boolean
}

export const profileIdentity = (props: ProfileIdentityContext, resources: ProfileIdentityResources): ProfileIdentity => {
  const { persona: userPersona, admin, verifiedPersona: userServicePersona, headerLoaded } = resources
  const personaId = props?.personaId
  const orgId = Number(props?.orgId) || undefined
  const currentUser = props?.currentUser
  const isClub = props?.isClub === true
  const hasVerifiedInfoFeatureToggle = props?.hasVerifiedInfoFeatureToggle === true
  const verifiedInfo = hasVerifiedInfoFeatureToggle ? findVerifiedInformation(userServicePersona) : null
  const verifiedInfoMismatchedFields = hasVerifiedInfoFeatureToggle ? getVerifiedInfoMismatchedFields(verifiedInfo, userPersona) : []

  const vm = {
    currentUser,
    personaId,
    orgId,
    persona: userPersona,
    admin,
    verifiedInfo,
    verifiedInfoFields: getVerifiedInfoFields(verifiedInfo),
    verifiedInfoMismatchedFields,
    verifiedInfoMismatch: verifiedInfoMismatchedFields.length > 0,
    hasVerifiedInfoFeatureToggle,
    isAdmin: isAdmin(admin),
    hasOwner: !!userPersona?.owner,
    editable: isOrphan(userPersona) || editableVerifiedInfo(
      currentUser,
      verifiedInfo,
      orgId,
      isClub,
      hasVerifiedInfoFeatureToggle
    ),
    pendingProfile: isPendingProfile(userPersona, admin),
    inviteStatus: inviteStatus(userPersona, admin),
    avatar: personaImageUrl(userPersona) || admin?.profile_image_url,
    title: name(verifiedInfo, userPersona) || adminName(admin),
    subtitle: admin ? adminRoles(admin) : [],
    headerLoaded,
    dob: verifiedInfo?.date_of_birth || userPersona?.date_of_birth,
    age: calculateVerifiedAge(verifiedInfo) || userPersona?.age?.toString() || '--',
    graduationYear: (verifiedInfo?.graduation_year || userPersona?.graduation_year)?.toString() || undefined,
    gender: verifiedInfo?.gender || userPersona?.gender || '--',
    email: emailAddress(userPersona, admin) || '--',
    emailId: primaryEmail(userPersona)?.id,
    phone: phoneNumber(userPersona, admin) || '--',
    address: fullAddress(userPersona) || '--',
    access: admin?.permission_type || '--',
    roles: admin?.positions?.join(', ') || ' --'
  }
  return vm
}

const calculateVerifiedAge = (verifiedInfo: VerifiedInformation | null): string | null | undefined => {
  if (!verifiedInfo?.verified ) return null
  if (verifiedInfo.date_of_birth) return AgeParser.fromIso(verifiedInfo.date_of_birth)
  return null
}

const adminRoles = (admin?: AdminManagement): string[] => {
  if (!admin) return []

  const access = admin.permission_type || 'No Access'
  const positions = [...(admin.positions || [])]

  if (!positions.length) positions.push('No Role')

  return [positions.join(', '), access]
}

const adminName = (admin?: AdminManagement): string =>
  [admin?.first_name, admin?.last_name].filter(Boolean).join(' ')

const isAdmin = (admin?: AdminManagement): boolean => {
  const hasGovSeasonAccess = admin?.governing_season_admin_access || admin?.governing_season_view_access
  return !!admin && !!(
    admin?.admin_invite_id ||
    admin?.permission_type !== 'No Access' ||
    !!admin?.positions?.length ||
    hasGovSeasonAccess
  )
}

const editableVerifiedInfo = (
  currentUser: ProfileIdentityCurrentUser | undefined,
  verifiedInfo: VerifiedInformation | null,
  orgId: number | undefined,
  isClub: boolean,
  hasVerifiedInfoFeatureToggle: boolean,
) => {
  if (!hasVerifiedInfoFeatureToggle) return false
  const userIsAdmin = currentUserIsAdmin(currentUser, orgId)
  return !!verifiedInfo && userIsAdmin && !isClub
}

const findVerifiedInformation = (persona: UserServicePersona | undefined): VerifiedInformation | null => {
  const verifiedInformation = persona?.verified_information || []
  const approvedVerifiedInformation = verifiedInformation.filter(vi => vi.verified === true)
  if (!approvedVerifiedInformation.length) return null

  return approvedVerifiedInformation.sort((a, b) => {
    const aApprovedAt = new Date(a.approved_at).getTime() || 0
    const bApprovedAt = new Date(b.approved_at).getTime() || 0
    if (aApprovedAt !== bApprovedAt) return bApprovedAt - aApprovedAt

    const aIsRootOrg = !a.root_org_id || a.root_org_id === a.org_id
    const bIsRootOrg = !b.root_org_id || b.root_org_id === b.org_id
    if (aIsRootOrg === bIsRootOrg) return 0
    return aIsRootOrg ? 1 : -1
  })[0]
}

const getVerifiedInfoFields = (verifiedInfo: VerifiedInformation | null): string[] => {
  if (!verifiedInfo) return []

  return [
    verifiedInfo.first_name ? 'first name' : null,
    verifiedInfo.middle_name ? 'middle name' : null,
    verifiedInfo.last_name ? 'last name' : null,
    verifiedInfo.suffix ? 'suffix' : null,
    verifiedInfo.gender ? 'gender' : null,
    verifiedInfo.date_of_birth ? 'date of birth' : null,
    verifiedInfo.graduation_year ? 'graduation year' : null,
  ].filter((field): field is string => !!field)
}

const getVerifiedInfoMismatchedFields = (
  verifiedInfo: VerifiedInformation | null,
  persona: UserPersona | undefined
): string[] => {
  if (!verifiedInfo || !persona) return []
  return [
    { label: 'first name', mismatch: !matchString(verifiedInfo.first_name, persona.first_name) },
    { label: 'last name', mismatch: !matchString(verifiedInfo.last_name, persona.last_name) },
    { label: 'suffix', mismatch: !matchSuffix(verifiedInfo.suffix, persona.suffix) },
    { label: 'middle name', mismatch: !matchString(verifiedInfo.middle_name, persona.middle_name) },
    { label: 'date of birth', mismatch: !matchString(verifiedInfo.date_of_birth, persona.date_of_birth) },
    { label: 'graduation year', mismatch: !matchString(verifiedInfo.graduation_year?.toString(), persona.graduation_year?.toString()) },
    { label: 'gender', mismatch: !matchString(verifiedInfo.gender, persona.gender) },
  ]
    .filter(field => field.mismatch)
    .map(field => field.label)
}

const matchString = (stringOne?: string | null, stringTwo?: string | null): boolean => {
  return (stringOne || '').trim().toLowerCase() === (stringTwo || '').trim().toLowerCase()
}

const matchSuffix = (suffixOne?: string | null, suffixTwo?: string | null): boolean => {
  return normalizeSuffix(suffixOne) === normalizeSuffix(suffixTwo)
}

const normalizeSuffix = (suffix?: string | null): string => {
  return (suffix || '').replace(/\./g, '').trim().toLowerCase()
}

const currentUserIsAdmin = (currentUser: ProfileIdentityCurrentUser | undefined, orgId: number | undefined): boolean => {
  const roleAssignments = currentUser?.role_assignments || []

  return roleAssignments.some(({ role, org_id }) => {
    return (role === 'org_admin' && org_id?.toString() === orgId?.toString()) ||
    ['platform_admin', 'third_north'].includes(role)
  })
}

const isPendingProfile = (
  persona: UserPersona | undefined,
  admin: AdminManagement | undefined
): boolean => {
  return !!(
    admin?.admin_invite_id ||
    persona?.orphan_invite_id ||
    isOrphan(persona)
  )
}

const isOrphan = (persona: UserPersona | undefined): boolean => {
  return persona?.type === 'orphan' ||
    persona?.persona_type === 'orphan'
}

const inviteStatus = (
  persona: UserPersona | undefined,
  admin: AdminManagement | undefined): string => {
    if (admin?.admin_invite_id) return 'invited'

    if (isOrphan(persona)){
      return persona?.orphan_invite_id ? 'invited' : 'uninvited'
    }

    return 'claimed'
  }

const name = (verifiedInfo: VerifiedInformation | null, personObj?: UserPersona): string => {
  if (!personObj) return ''

  const firstName = verifiedInfo?.first_name || personObj.first_name || ''
  const lastName = verifiedInfo?.last_name || personObj.last_name || ''
  const suffix = verifiedInfo?.suffix || personObj?.suffix || ''

  return [firstName, lastName, suffix].filter(Boolean).join(' ')
}

const personaImageUrl = (persona?: UserPersona): string | undefined => {
  return persona?.profile_images?.find(pi => pi.image_type === 'crop_icon')?.url || ''
}

const emailAddress = (persona?: UserPersona, admin?: AdminManagement): string => {
  return primaryEmail(persona)?.address || admin?.email_address || ''
}

const primaryEmail = (persona?: UserPersona): UserPersona['email_addresses'][number] | undefined => {
  return persona?.email_addresses?.find(e => (e.is_primary === true))
}

const phoneNumber = (persona?: UserPersona, admin?: AdminManagement): string => {
  return persona?.phone_numbers?.find(p => p.phone_type === 'cellPhone')?.number || admin?.phone_number || ''
}

const fullAddress = (persona?: UserPersona): string => {
  if (!persona?.addresses?.length) return ''
  const { street_1, zip, city, state_or_province, country } = persona.addresses[0]
  return `${street_1}, ${city}, ${state_or_province} ${zip}, ${country}`.trim()
}
